import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Table,
  Row,
  Col,
  Alert,
  Tabs,
  Tab,
  Badge,
  Spinner,
  OverlayTrigger,
  Tooltip,
  InputGroup,
} from "react-bootstrap";
import { doc, getDoc, setDoc, getFirestore } from "firebase/firestore";
import { 
  FaSave, 
  FaPlus, 
  FaTrash,
  FaCalculator,
  FaBookOpen,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaCog,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheck,
  FaUndo
} from "react-icons/fa";
import { BsFileText, BsGear, BsTable } from "react-icons/bs";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

const TIPOS_CUENTA = {
  GASTOS: {
    titulo: "Gastos de Personal (6)",
    icono: <FaMoneyBillWave />,
    color: "#28a745",
    descripcion: "Cuentas para registrar los gastos de personal y cargas sociales",
    opciones: {
      sueldos: "Sueldos y Salarios",
      asignacion_familiar: "Asignación Familiar",
      horas_extras: "Horas Extras",
      bonificaciones: "Bonificaciones",
      gratificaciones: "Gratificaciones",
      vacaciones: "Vacaciones",
      cts: "CTS",
      // Cargas sociales
      essalud: "ESSALUD",
      seguro_vida: "Seguro de Vida Ley",
      sctr_salud: "SCTR Salud",
      sctr_pension: "SCTR Pensión",
      senati: "SENATI",
    },
  },
  TRIBUTOS: {
    titulo: "Tributos y Aportes (40)",
    icono: <FaFileInvoiceDollar />,
    color: "#dc3545",
    descripcion: "Cuentas para registrar tributos y aportes por pagar",
    opciones: {
      quinta_categoria: "Renta 5ta Categoría",
      essalud_pagar: "ESSALUD por Pagar",
      essalud_vida: "ESSALUD Vida",
      onp_pagar: "ONP por Pagar",
      afp_pagar: "AFP por Pagar",
      sctr_salud_pagar: "SCTR Salud por Pagar",
      sctr_pension_pagar: "SCTR Pensión por Pagar",
      senati_pagar: "SENATI por Pagar",
      vida_ley_pagar: "Seguro Vida Ley (Amortización/Pasivo)",
    },
  },
  REMUNERACIONES: {
    titulo: "Remuneraciones por Pagar (41)",
    icono: <FaCalculator />,
    color: "#007bff",
    descripcion: "Cuentas para registrar remuneraciones pendientes de pago",
    opciones: {
      remuneraciones_pagar: "Sueldos por Pagar",
      gratificaciones_pagar: "Gratificaciones por Pagar",
      vacaciones_pagar: "Vacaciones por Pagar",
      cts_pagar: "CTS por Pagar",
      bonificaciones_pagar: "Bonificaciones por Pagar",
    },
  },
  ACTIVOS: {
    titulo: "Activos y Bancos (1)",
    icono: <FaMoneyBillWave />,
    color: "#6f42c1",
    descripcion: "Cuentas de bancos y descuentos al personal por recuperar",
    opciones: {
      bancos: "Bancos - Cuenta Operativa",
      adelantos_descuento: "Adelantos de Remuneraciones",
      prestamos_descuento: "Préstamos al Personal",
    },
  },
};

const CUENTAS_BASE = {
  // Gastos de Personal
  sueldos: { codigo: "621101", descripcion: "SUELDOS Y SALARIOS", nivel: "4" },
  asignacion_familiar: {
    codigo: "621102",
    descripcion: "ASIGNACIÓN FAMILIAR",
    nivel: "4",
  },
  horas_extras: { codigo: "621103", descripcion: "HORAS EXTRAS", nivel: "4" },
  bonificaciones: {
    codigo: "621104",
    descripcion: "BONIFICACIONES",
    nivel: "4",
  },
  gratificaciones: {
    codigo: "621501",
    descripcion: "GRATIFICACIONES",
    nivel: "4",
  },
  vacaciones: { codigo: "621401", descripcion: "VACACIONES", nivel: "4" },
  cts: {
    codigo: "621201",
    descripcion: "COMPENSACIÓN POR TIEMPO DE SERVICIOS",
    nivel: "4",
  },

  // Cargas Sociales
  essalud: {
    codigo: "627101",
    descripcion: "RÉGIMEN DE PRESTACIONES DE SALUD",
    nivel: "4",
  },
  seguro_vida: {
    codigo: "627102",
    descripcion: "SEGURO DE VIDA LEY",
    nivel: "4",
  },
  sctr_salud: { codigo: "627103", descripcion: "SCTR SALUD", nivel: "4" },
  sctr_pension: { codigo: "627104", descripcion: "SCTR PENSIÓN", nivel: "4" },
  senati: { codigo: "627105", descripcion: "CONTRIBUCIÓN SENATI", nivel: "4" },

  // Tributos y Aportes por Pagar
  quinta_categoria: {
    codigo: "401701",
    descripcion: "RENTA DE QUINTA CATEGORÍA",
    nivel: "4",
    contrapartida_tipo: "PASIVO"
  },
  essalud_pagar: {
    codigo: "403101",
    descripcion: "ESSALUD POR PAGAR",
    nivel: "4",
    contrapartida_tipo: "PASIVO"
  },
  essalud_vida: {
    codigo: "403902",
    descripcion: "ESSALUD VIDA",
    nivel: "4",
    contrapartida_tipo: "PASIVO"
  },
  onp_pagar: { codigo: "403201", descripcion: "ONP POR PAGAR", nivel: "4" },
  afp_pagar: { codigo: "403202", descripcion: "AFP POR PAGAR", nivel: "4" },
  sctr_salud_pagar: {
    codigo: "403301",
    descripcion: "SCTR SALUD POR PAGAR",
    nivel: "4",
  },
  sctr_pension_pagar: {
    codigo: "403302",
    descripcion: "SCTR PENSIÓN POR PAGAR",
    nivel: "4",
  },
  senati_pagar: {
    codigo: "403401",
    descripcion: "SENATI POR PAGAR",
    nivel: "4",
  },
  vida_ley_pagar: {
    codigo: "1821",
    descripcion: "SEGUROS PAGADOS POR ANTICIPADO",
    nivel: "4",
    contrapartida_tipo: "ACTIVO"
  },

  // Remuneraciones por Pagar
  remuneraciones_pagar: {
    codigo: "411101",
    descripcion: "SUELDOS POR PAGAR",
    nivel: "4",
  },
  gratificaciones_pagar: {
    codigo: "411201",
    descripcion: "GRATIFICACIONES POR PAGAR",
    nivel: "4",
  },
  vacaciones_pagar: {
    codigo: "411301",
    descripcion: "VACACIONES POR PAGAR",
    nivel: "4",
  },
  cts_pagar: { codigo: "411401", descripcion: "CTS POR PAGAR", nivel: "4" },
  bonificaciones_pagar: {
    codigo: "411501",
    descripcion: "BONIFICACIONES POR PAGAR",
    nivel: "4",
  },
  bancos: {
    codigo: "104101",
    descripcion: "CUENTAS CORRIENTES OPERATIVAS",
    nivel: "4",
  },
  adelantos_descuento: {
    codigo: "141201",
    descripcion: "ADELANTO DE REMUNERACIONES",
    nivel: "4",
  },
  prestamos_descuento: {
    codigo: "141101",
    descripcion: "PRÉSTAMOS AL PERSONAL",
    nivel: "4",
  },
};

const ConfiguracionContable = ({ usuario }) => {
  // Inicializar con valores por defecto
  const [config, setConfig] = useState({
    cuentas: { ...CUENTAS_BASE }, // Usamos la configuración por defecto inicialmente
    destinos: {
      administrativo: { codigo: "91", descripcion: "ADMINISTRATIVO" },
      ventas: { codigo: "92", descripcion: "VENTAS" },
      produccion: { codigo: "93", descripcion: "PRODUCCIÓN" },
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("GASTOS");
  const [esConfiguracionNueva, setEsConfiguracionNueva] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [nuevaCuenta, setNuevaCuenta] = useState({
    tipo: "",
    nombre: "",
    codigo: "",
    descripcion: "",
    nivel: "4"
  });
  const [mostrarFormNuevaCuenta, setMostrarFormNuevaCuenta] = useState(false);

  useEffect(() => {
    if (usuario?.ruc) {
      cargarConfiguracion();
    } else {
      setLoading(false);
    }
  }, [usuario?.ruc]);

  // Función para añadir nueva cuenta
  const handleAgregarCuenta = () => {
    // Validar que todos los campos necesarios estén completos
    if (!nuevaCuenta.tipo || !nuevaCuenta.nombre || !nuevaCuenta.codigo || !nuevaCuenta.descripcion) {
      Swal.fire({
        icon: "error",
        title: "Datos incompletos",
        text: "Por favor complete todos los campos para la nueva cuenta",
        confirmButtonColor: "#8B4513"
      });
      return;
    }

    // Validar que el tipo no exista ya en alguna categoría
    let tipoExistente = false;
    Object.values(TIPOS_CUENTA).forEach(categoria => {
      if (categoria.opciones[nuevaCuenta.tipo]) {
        tipoExistente = true;
      }
    });

    if (tipoExistente) {
      Swal.fire({
        icon: "error",
        title: "Tipo existente",
        text: "El identificador de cuenta ya existe. Use otro.",
        confirmButtonColor: "#8B4513"
      });
      return;
    }

    // Añadir cuenta a la configuración
    const tipoActivo = activeTab;
    
    // Actualizar TIPOS_CUENTA (solo en memoria para esta sesión)
    TIPOS_CUENTA[tipoActivo].opciones[nuevaCuenta.tipo] = nuevaCuenta.nombre;
    
    // Actualizar el estado de la configuración
    setConfig(prev => ({
      ...prev,
      cuentas: {
        ...prev.cuentas,
        [nuevaCuenta.tipo]: {
          codigo: nuevaCuenta.codigo,
          descripcion: nuevaCuenta.descripcion,
          nivel: nuevaCuenta.nivel
        }
      }
    }));

    // Limpiar el formulario
    setNuevaCuenta({
      tipo: "",
      nombre: "",
      codigo: "",
      descripcion: "",
      nivel: "4"
    });
    
    // Ocultar el formulario después de añadir
    setMostrarFormNuevaCuenta(false);
    
    Swal.fire({
      icon: "success",
      title: "Cuenta añadida",
      text: "La cuenta se ha añadido correctamente a la configuración.",
      confirmButtonColor: "#8B4513"
    });
  };

  const cargarConfiguracion = async () => {
    try {
      setLoading(true);
      const configRef = doc(db, "pl_config_contable", usuario.ruc);
      const configDoc = await getDoc(configRef);
      
      if (configDoc.exists()) {
        // Si existe configuración, la cargamos
        const datosConfig = configDoc.data();
        if (datosConfig.cuentas && Object.keys(datosConfig.cuentas).length > 0) {
          // Convertir timestamps si existen
          if (datosConfig.updated_at) {
            datosConfig.updated_at = datosConfig.updated_at.toDate();
            setLastSaved(datosConfig.updated_at);
          }
          setConfig(prev => ({
            ...prev,
            ...datosConfig
          }));
          setEsConfiguracionNueva(false);
        } else {
          // Si existe el documento pero no tiene cuentas, mantener los valores por defecto
          console.log("El documento existe pero no contiene cuentas. Usando configuración por defecto.");
        }
      } else {
        // Si no existe configuración, mostramos mensaje informativo
        console.log("No se encontró configuración previa. Usando configuración por defecto.");
        // Ya tenemos los valores por defecto en el estado inicial
      }
    } catch (error) {
      console.error("Error al cargar configuración:", error);
      
      // No mostramos error por documento no encontrado, solo otros errores
      if (error.code !== 'not-found') {
        Swal.fire({
          icon: 'warning',
          title: 'Aviso',
          text: 'Se ha cargado la configuración por defecto ya que no se encontró una configuración previa.',
          confirmButtonColor: "#8B4513"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const validarNivelCuenta = (cuenta) => {
    // Si la cuenta no existe o falta algún campo requerido
    if (!cuenta || typeof cuenta !== 'object') {
      return false;
    }

    if (!cuenta.codigo || !cuenta.nivel || !cuenta.descripcion) {
      return false;
    }
    
    const codigoLimpio = cuenta.codigo.trim().replace(/\D/g, ''); // Eliminar caracteres no numéricos
    const nivelNumerico = parseInt(cuenta.nivel);

    // Validar que el código tenga al menos un número
    if (codigoLimpio.length === 0) {
      return false;
    }

    // La longitud ahora es una sugerencia, no una restricción estricta
    // ya que formatearCodigo se encargará de ajustar según sea necesario
    return true;
  };

  const handleGuardarConfiguracion = async () => {
    setSaving(true);
    try {
      if (!usuario?.ruc) {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se puede guardar la configuración sin un RUC de empresa válido",
          confirmButtonColor: "#8B4513"
        });
        return;
      }

      // Solo validar las cuentas que tienen código y descripción
      const cuentasInvalidas = [];
      
      Object.entries(config.cuentas).forEach(([tipo, cuenta]) => {
        // Solo validar si la cuenta tiene código (está siendo configurada)
        if (cuenta.codigo && cuenta.codigo.trim() !== '') {
          if (!validarNivelCuenta(cuenta)) {
            // Buscar el nombre amigable del tipo de cuenta
            let nombreCuenta = tipo;
            
            Object.entries(TIPOS_CUENTA).forEach(([grupo, datos]) => {
              if (datos.opciones[tipo]) {
                nombreCuenta = `${datos.opciones[tipo]} (${datos.titulo})`;
              }
            });
            
            cuentasInvalidas.push(nombreCuenta);
          }
        }
      });

      if (cuentasInvalidas.length > 0) {
        await Swal.fire({
          icon: "error",
          title: "Error de validación",
          html: `Las siguientes cuentas configuradas tienen códigos inválidos:<br><br>${cuentasInvalidas.join("<br>")}`,
          confirmButtonColor: "#8B4513"
        });
        return;
      }

      // Formatear los códigos de cuentas según el nivel
      const cuentasFormateadas = {};
      Object.entries(config.cuentas).forEach(([tipo, cuenta]) => {
        if (cuenta.codigo && cuenta.codigo.trim() !== '') {
          const nivel = parseInt(cuenta.nivel || '4');
          cuentasFormateadas[tipo] = {
            ...cuenta,
            codigo: formatearCodigo(cuenta.codigo, nivel)
          };
        } else {
          // Mantener cuentas sin código
          cuentasFormateadas[tipo] = cuenta;
        }
      });

      const configActualizada = {
        ...config,
        cuentas: cuentasFormateadas,
        ruc_empresa: usuario.ruc,
        updated_at: new Date(),
        created_at: esConfiguracionNueva ? new Date() : (config.created_at || new Date()),
      };

      const configRef = doc(db, "pl_config_contable", usuario.ruc);
      await setDoc(configRef, configActualizada);

      // Actualizar el estado local después de guardar exitosamente
      setConfig(configActualizada);
      setEsConfiguracionNueva(false);
      setLastSaved(new Date());

      await Swal.fire({
        icon: "success",
        title: "Configuración guardada",
        text: "La configuración contable se ha actualizado correctamente",
        confirmButtonColor: "#8B4513",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Error al guardar:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo guardar la configuración: " + (error.message || "Error desconocido"),
        confirmButtonColor: "#8B4513"
      });
    } finally {
      setSaving(false);
    }
  };

  const formatearCodigo = (codigo, nivel) => {
    if (!codigo) return '';
    
    // Limpiar el código de espacios y permitir que comience con 1, 4 o 6
    const codigoLimpio = codigo.replace(/[^\d]/g, '');
    
    // Validar que el primer dígito sea 1, 4, 6 o 9 (centros de costo)
    if (codigoLimpio.length > 0 && !['1', '4', '6', '9'].includes(codigoLimpio[0])) {
      // Si no es un prefijo válido, no lo restringimos agresivamente aquí 
      // pero el motor de asientos lo validará después
    }
    
    // Truncar según el nivel
    if (codigoLimpio.length > nivel) {
      return codigoLimpio.substring(0, nivel);
    }
    
    return codigoLimpio;
  };

  const handleChangeCuenta = (tipo, campo, valor) => {
    setConfig(prev => ({
      ...prev,
      cuentas: {
        ...prev.cuentas,
        [tipo]: {
          ...prev.cuentas[tipo] || {}, // Inicializar si no existe
          [campo]: valor,
        },
      },
    }));
  };

  const handleEliminarCuenta = (tipo) => {
    Swal.fire({
      title: "¿Eliminar cuenta?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        setConfig((prev) => {
          const nuevasCuentas = { ...prev.cuentas };
          delete nuevasCuentas[tipo];
          return {
            ...prev,
            cuentas: nuevasCuentas,
          };
        });
      }
    });
  };

  const handleRestablecer = () => {
    Swal.fire({
      title: "¿Restablecer configuración?",
      text: "Se cargarán las cuentas predeterminadas del plan contable",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#8B4513",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, cargar cuentas base",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        setConfig((prev) => ({
          ...prev,
          cuentas: { ...CUENTAS_BASE }, // Usamos copia para evitar mutación
        }));
        
        Swal.fire({
          icon: "info",
          title: "Configuración restablecida",
          text: "Se han cargado las cuentas predeterminadas. Recuerde guardar los cambios.",
          confirmButtonColor: "#8B4513"
        });
      }
    });
  };

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const totalCuentas = Object.keys(config.cuentas).length;
    const cuentasConfiguradas = Object.values(config.cuentas).filter(
      cuenta => cuenta.codigo && cuenta.codigo.trim() !== ''
    ).length;
    
    return {
      totalCuentas,
      cuentasConfiguradas,
      porcentajeCompletado: totalCuentas > 0 ? Math.round((cuentasConfiguradas / totalCuentas) * 100) : 0
    };
  };

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        }}
      >
        <div className="text-center">
          <Spinner
            animation="border"
            style={{ color: "#8B4513", width: "3rem", height: "3rem" }}
          />
          <p className="mt-3 mb-0" style={{ color: "#8B4513" }}>
            Cargando configuración contable...
          </p>
        </div>
      </div>
    );
  }

  if (!usuario?.ruc) {
    return (
      <div
        className="min-vh-100"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
          minHeight: "100vh",
          padding: "20px 0",
        }}
      >
        <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
          <Alert variant="warning" className="text-center">
            <FaExclamationTriangle className="me-2" />
            No se puede cargar la configuración contable sin un RUC de empresa válido.
          </Alert>
        </Container>
      </div>
    );
  }

  const estadisticas = calcularEstadisticas();

  const renderTabContent = (tipoGrupo) => {
    const grupo = TIPOS_CUENTA[tipoGrupo];
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h5 className="mb-1" style={{ color: "#8B4513" }}>
              {grupo.icono} {grupo.titulo}
            </h5>
            <p className="text-muted mb-0 small">
              {grupo.descripcion}
            </p>
          </div>
          <Button 
            variant="outline-primary"
            size="sm"
            onClick={() => setMostrarFormNuevaCuenta(!mostrarFormNuevaCuenta)}
            style={{
              borderColor: "#8B4513",
              color: "#8B4513",
              borderRadius: "10px"
            }}
          >
            <FaPlus className="me-1" /> 
            {mostrarFormNuevaCuenta ? "Cancelar" : "Añadir cuenta"}
          </Button>
        </div>
  
        {mostrarFormNuevaCuenta && (
          <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: "15px" }}>
            <Card.Header
              className="text-white py-3"
              style={{
                background: `linear-gradient(135deg, ${grupo.color} 0%, ${grupo.color}dd 100%)`,
                borderRadius: "15px 15px 0 0"
              }}
            >
              <div className="d-flex align-items-center">
                {grupo.icono}
                <span className="ms-2 fw-semibold">Nueva Cuenta para {grupo.titulo}</span>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      <BsGear className="me-1" />
                      Identificador (para el sistema)
                    </Form.Label>
                    <Form.Control
                      type="text" 
                      placeholder="Ej: essalud_vida" 
                      value={nuevaCuenta.tipo}
                      onChange={(e) => setNuevaCuenta({...nuevaCuenta, tipo: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                      style={{ borderRadius: "10px" }}
                    />
                    <Form.Text className="text-muted">
                      Identificador único sin espacios ni caracteres especiales
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      <BsFileText className="me-1" />
                      Nombre (para mostrar)
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Ej: EsSalud Vida" 
                      value={nuevaCuenta.nombre}
                      onChange={(e) => setNuevaCuenta({...nuevaCuenta, nombre: e.target.value})}
                      style={{ borderRadius: "10px" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      <FaCalculator className="me-1" />
                      Código Contable
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Ej: 403902" 
                      value={nuevaCuenta.codigo}
                      onChange={(e) => setNuevaCuenta({...nuevaCuenta, codigo: e.target.value})}
                      style={{ borderRadius: "10px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      <FaBookOpen className="me-1" />
                      Descripción
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Ej: ESSALUD VIDA" 
                      value={nuevaCuenta.descripcion}
                      onChange={(e) => setNuevaCuenta({...nuevaCuenta, descripcion: e.target.value})}
                      style={{ borderRadius: "10px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      <BsTable className="me-1" />
                      Nivel
                    </Form.Label>
                    <Form.Select
                      value={nuevaCuenta.nivel}
                      onChange={(e) => setNuevaCuenta({...nuevaCuenta, nivel: e.target.value})}
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="2">2 dígitos</option>
                      <option value="3">3 dígitos</option>
                      <option value="4">4 dígitos</option>
                      <option value="5">5 dígitos</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <div className="text-end">
                <Button 
                  variant="secondary" 
                  className="me-2" 
                  onClick={() => setMostrarFormNuevaCuenta(false)}
                  style={{ borderRadius: "10px" }}
                >
                  Cancelar
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleAgregarCuenta}
                  style={{
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                    border: "none"
                  }}
                >
                  <FaPlus className="me-1" /> Añadir Cuenta
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}
        
        <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
          <div style={{ maxHeight: "500px", overflowY: "auto" }}>
            <Table responsive hover className="mb-0" style={{ fontSize: "0.9rem" }}>
              <thead
                style={{
                  backgroundColor: "#f8f9fa",
                  position: "sticky",
                  top: 0,
                  zIndex: 10
                }}
              >
                <tr>
                  <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "20%" }}>
                    <FaBookOpen className="me-1" />
                    Concepto
                  </th>
                  <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "20%" }}>
                    <FaCalculator className="me-1" />
                    Código
                  </th>
                  <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                    <BsFileText className="me-1" />
                    Descripción
                  </th>
                  <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "15%" }}>
                    <BsTable className="me-1" />
                    Nivel
                  </th>
                  {tipoGrupo === 'TRIBUTOS' && (
                    <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "15%" }}>
                      <FaCog className="me-1" />
                      Contrapartida
                    </th>
                  )}
                  <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: "10%" }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grupo.opciones).map(([tipo, nombre]) => {
                  const cuenta = config.cuentas[tipo] || {};
                  const configurada = cuenta.codigo && cuenta.codigo.trim() !== '';
                  return (
                    <tr key={tipo} style={{ borderBottom: "1px solid #f1f3f4" }}>
                      <td className="py-3 px-3">
                        <div className="d-flex align-items-center">
                          <div className="fw-bold text-dark">{nombre}</div>
                          {configurada && (
                            <Badge 
                              className="ms-2"
                              style={{
                                background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                fontSize: "0.7rem"
                              }}
                            >
                              <FaCheck size="10px" />
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <Form.Control
                          type="text"
                          value={cuenta.codigo || ""}
                          onChange={(e) =>
                            handleChangeCuenta(tipo, "codigo", e.target.value)
                          }
                          placeholder="Ej: 6211"
                          style={{ borderRadius: "8px", fontSize: "0.9rem" }}
                        />
                      </td>
                      <td className="py-3 px-3">
                        <Form.Control
                          type="text"
                          value={cuenta.descripcion || ""}
                          onChange={(e) =>
                            handleChangeCuenta(tipo, "descripcion", e.target.value)
                          }
                          style={{ borderRadius: "8px", fontSize: "0.9rem" }}
                        />
                      </td>
                      <td className="py-3 px-3">
                        <Form.Select
                          value={cuenta.nivel || "4"}
                          onChange={(e) =>
                            handleChangeCuenta(tipo, "nivel", e.target.value)
                          }
                          style={{ borderRadius: "8px", fontSize: "0.9rem" }}
                        >
                          <option value="2">2 dígitos</option>
                          <option value="3">3 dígitos</option>
                          <option value="4">4 dígitos</option>
                          <option value="5">5 dígitos</option>
                        </Form.Select>
                      </td>
                      {tipoGrupo === 'TRIBUTOS' && (
                        <td className="py-3 px-3">
                          <Form.Select
                            value={cuenta.contrapartida_tipo || "PASIVO"}
                            onChange={(e) =>
                              handleChangeCuenta(tipo, "contrapartida_tipo", e.target.value)
                            }
                            style={{ borderRadius: "8px", fontSize: "0.85rem" }}
                          >
                            <option value="PASIVO">PASIVO (40/41)</option>
                            <option value="ACTIVO">ACTIVO (18)</option>
                          </Form.Select>
                        </td>
                      )}
                      <td className="py-3 px-3 text-center">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Eliminar cuenta</Tooltip>}
                        >
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleEliminarCuenta(tipo)}
                            style={{ borderRadius: "8px" }}
                          >
                            <FaTrash size="12px" />
                          </Button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card>
      </>
    );
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <BsGear className="me-3" />
                Configuración Contable
              </h2>
              <p className="mb-0 opacity-75">
                Configure las cuentas contables para el registro de planillas
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con información */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        color: "white",
                      }}
                    >
                      <FaCog size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">
                      Plan Contable de Planillas
                    </h5>
                    <p className="text-muted mb-0 small">
                      Configure las cuentas contables según el nivel de detalle requerido por la empresa
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      {lastSaved && (
                        <Badge
                          style={{
                            background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                            fontSize: "0.8rem",
                            padding: "6px 12px"
                          }}
                        >
                          <FaCheck className="me-1" />
                          Actualizado: {lastSaved.toLocaleString()}
                        </Badge>
                      )}
                      {esConfiguracionNueva && (
                        <Badge
                          style={{
                            background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                            fontSize: "0.8rem",
                            padding: "6px 12px"
                          }}
                        >
                          <FaExclamationTriangle className="me-1" />
                          Configuración inicial
                        </Badge>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estadísticas */}
            <Row className="mb-4">
              <Col md={4}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <BsTable size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.totalCuentas}</h4>
                    <p className="mb-0">Total Cuentas</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaCheck size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.cuentasConfiguradas}</h4>
                    <p className="mb-0">Configuradas</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaInfoCircle size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.porcentajeCompletado}%</h4>
                    <p className="mb-0">Completado</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Alertas informativas */}
            {esConfiguracionNueva && (
              <Alert 
                variant="info" 
                className="mb-4"
                style={{ borderRadius: "15px", border: "none" }}
              >
                <FaInfoCircle className="me-2" />
                <strong>Configuración inicial:</strong> Se han cargado los valores predeterminados. 
                Modifique las cuentas según sus necesidades y guarde los cambios.
              </Alert>
            )}
            
            <Alert 
              variant="secondary" 
              className="mb-4"
              style={{ borderRadius: "15px", border: "none" }}
            >
              <FaExclamationTriangle className="me-2" />
              Configure las cuentas contables según el nivel de detalle requerido
              por la empresa. El nivel determina la cantidad de dígitos del código
              contable.
            </Alert>

            {/* Tabs de configuración */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-0"
                style={{ padding: "0 20px", paddingTop: "20px" }}
              >
                {Object.keys(TIPOS_CUENTA).map((tipo) => {
                  const grupo = TIPOS_CUENTA[tipo];
                  return (
                    <Tab 
                      key={tipo} 
                      eventKey={tipo} 
                      title={
                        <span>
                          {grupo.icono} {grupo.titulo}
                        </span>
                      }
                    >
                      <div className="p-4">
                        {renderTabContent(tipo)}
                      </div>
                    </Tab>
                  );
                })}
              </Tabs>
            </Card>

            {/* Botones de acción */}
            <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4 text-center">
                <div className="d-flex gap-3 justify-content-center">
                  <Button
                    variant="outline-secondary"
                    onClick={handleRestablecer}
                    className="px-4 py-2"
                    style={{
                      borderRadius: "15px",
                      borderColor: "#D2691E",
                      color: "#D2691E"
                    }}
                  >
                    <FaUndo className="me-2" />
                    Restablecer
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleGuardarConfiguracion}
                    disabled={saving}
                    size="lg"
                    className="px-5 py-3"
                    style={{
                      borderRadius: "15px",
                      background: saving 
                        ? "linear-gradient(135deg, #6c757d 0%, #495057 100%)"
                        : "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                      border: "none",
                      fontSize: "1.1rem",
                      fontWeight: "600",
                      minWidth: "200px"
                    }}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Guardar Configuración
                      </>
                    )}
                  </Button>
                </div>
                {lastSaved && (
                  <p className="text-muted mt-3 mb-0 small">
                    <FaInfoCircle className="me-1" />
                    Los cambios se aplicarán en los próximos registros contables
                  </p>
                )}
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ConfiguracionContable;
